﻿import { useEffect, useMemo } from "react";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  DashedDivider,
  RegistrationJourneyButtons,
  RegistrationStepPage,
} from "components";
import { ExamFormat } from "api";
import { useRegistrationJourney } from "journey";
import { getAge } from "utils";
import { GTag } from "hooks";
import { CandidateDetailsFormData, getValidationSchema } from "./validation";
import { PersonalDetails } from "./sections/PersonalDetails";
import { AddressDetails } from "./sections/AddressDetails";
import { ContactDetails } from "./sections/ContactDetails";
import { SpecialRequirements } from "./sections/SpecialRequirements";
import { PermissionToLeave } from "./sections/PermissionToLeave";
import { InformationNotes } from "./sections/InformationNotes";

export const CandidateDetails = () => {
  const { t, i18n } = useTranslation();
  const schema = useMemo(() => getValidationSchema(t), [t]);
  const {
    candidateDetails,
    selectedExam,
    marketingConsent,
    provideCandidateDetails,
  } = useRegistrationJourney();

  const { handleSubmit, control, trigger, formState, watch } =
    useForm<CandidateDetailsFormData>({
      mode: "all",
      resolver: yupResolver(schema),
      defaultValues: { ...candidateDetails, marketingConsent },
      context: { examDate: selectedExam?.examDate },
    });

  // eslint-disable-next-line react-hooks/incompatible-library
  const [dateOfBirth] = watch(["dateOfBirth"]);
  const age = getAge(selectedExam?.examDate, dateOfBirth);
  const showSpecialRequirements = selectedExam?.examFormat == ExamFormat.Pb;
  const showPermissionToLeave = age && age < 18;

  const onSubmit = ({
    marketingConsent,
    ...data
  }: CandidateDetailsFormData) => {
    provideCandidateDetails(data, marketingConsent);
    GTag.addShippingInfo(selectedExam);
  };

  useEffect(() => {
    if (formState.isSubmitted) {
      //required to refresh error messages when language changes
      trigger();
    }
  }, [i18n.language, formState.isSubmitted, trigger]);

  return (
    <RegistrationStepPage stepIndex={2}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h3" sx={{ mb: 8 }}>
          {t("details.title")}
        </Typography>

        <PersonalDetails control={control} />

        <AddressDetails control={control} />

        <ContactDetails control={control} age={age} />

        {showSpecialRequirements && (
          <SpecialRequirements control={control} watch={watch} />
        )}

        {showPermissionToLeave && <PermissionToLeave control={control} />}

        <InformationNotes control={control} />

        <DashedDivider sx={{ mb: 4 }} />
        <RegistrationJourneyButtons />
      </form>
    </RegistrationStepPage>
  );
};
